import { useSyncExternalStore } from 'react';

/**
 * PWA state (T2.9): service-worker lifecycle, install prompt and connectivity.
 * `main.tsx` wires the real service worker in; screens only read this store,
 * so tests never touch `virtual:pwa-register`.
 */
export interface PwaState {
  /** A new version is installed and waits for the player to reload. */
  needRefresh: boolean;
  /** The game is cached and plays without network. */
  offlineReady: boolean;
  /** The browser offered an install prompt we can trigger. */
  canInstall: boolean;
  /** Running as an installed app. */
  installed: boolean;
  online: boolean;
}

/** Shape of `registerSW` from `virtual:pwa-register`. */
export type RegisterSW = (options: {
  onNeedRefresh?: () => void;
  onOfflineReady?: () => void;
}) => (reloadPage?: boolean) => Promise<void>;

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const hasWindow = typeof window !== 'undefined';

function standalone(): boolean {
  if (!hasWindow) return false;
  return window.matchMedia?.('(display-mode: standalone)').matches === true || (navigator as { standalone?: boolean }).standalone === true;
}

let state: PwaState = {
  needRefresh: false,
  offlineReady: hasWindow && !!navigator.serviceWorker?.controller,
  canInstall: false,
  installed: standalone(),
  online: hasWindow ? navigator.onLine !== false : true,
};
let updateSW: ((reloadPage?: boolean) => Promise<void>) | null = null;
let installEvent: InstallPromptEvent | null = null;
let started = false;
const listeners = new Set<() => void>();

function patch(next: Partial<PwaState>): void {
  state = { ...state, ...next };
  for (const l of listeners) l();
}

export function initPwa(register: RegisterSW | null): void {
  if (started || !hasWindow) return;
  started = true;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    installEvent = e as InstallPromptEvent;
    patch({ canInstall: true });
  });
  window.addEventListener('appinstalled', () => {
    installEvent = null;
    patch({ canInstall: false, installed: true });
  });
  window.addEventListener('online', () => patch({ online: true }));
  window.addEventListener('offline', () => patch({ online: false }));
  if (register && 'serviceWorker' in navigator) {
    updateSW = register({
      onNeedRefresh: () => patch({ needRefresh: true }),
      onOfflineReady: () => patch({ offlineReady: true }),
    });
  }
}

export function usePwa(): PwaState {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
  );
}

/** Activates the waiting service worker and reloads (the caller saves first). */
export async function applyUpdate(): Promise<void> {
  if (updateSW) await updateSW(true);
  else window.location.reload();
}

export function dismissUpdate(): void {
  patch({ needRefresh: false });
}

export async function installApp(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!installEvent) return 'unavailable';
  const e = installEvent;
  installEvent = null;
  patch({ canInstall: false });
  await e.prompt();
  const { outcome } = await e.userChoice;
  if (outcome === 'accepted') patch({ installed: true });
  return outcome;
}

/** iOS Safari has no install prompt: the player uses Share → Add to Home Screen. */
export function isIos(): boolean {
  return hasWindow && /iphone|ipad|ipod/i.test(navigator.userAgent);
}

/** Test hook. */
export function setPwaForTests(next: Partial<PwaState>): void {
  patch(next);
}
